import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, IconButton,
  CircularProgress, Alert, Paper, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip,
  List, ListItem, ListItemText, ListItemButton, Avatar,
  Divider, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  VideoCall as InterviewIcon,
  Refresh as RefreshIcon,
  Assignment as TranscriptIcon,
  UploadFile as UploadIcon,
  DeleteOutline as DeleteIcon,
  AutoAwesome as AiIcon,
  QuestionAnswer as QuestionsIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import AtsPositionInterviewQuestionsDialog from './AtsPositionInterviewQuestionsDialog';

const STATUS_CONFIG = {
  pending:            { labelEs: 'Bot en reunión',          labelEn: 'Bot in meeting',        color: 'info'    },
  pending_assignment: { labelEs: 'Asignar participantes',   labelEn: 'Assign participants',   color: 'warning' },
  completed:          { labelEs: 'Completada',              labelEn: 'Completed',             color: 'success' },
  error:              { labelEs: 'Error',                   labelEn: 'Error',                 color: 'error'   },
};

/**
 * Modal de entrevistas de una etapa (botón "Entrevistas" de la columna del
 * kanban, o "Registrar entrevista" del drawer).
 *
 * Vista de candidatos: además de la lista, el bloque de preguntas de
 * entrevista del CARGO (con IA o a mano). Es el único lugar donde se definen;
 * las respuestas de cada candidato se anotan en su ficha (drawer).
 *
 * @param {Object} position - Vacante (para leer position.interviewQuestions)
 * @param {Function} onPositionChange - Recibe un patch de la vacante (p. ej.
 *   { interviewQuestions }) para reflejarlo en pantalla sin recargar
 */
const AtsInterviewDialog = ({
  open, onClose, applications, stage, positionId, position = null, onPositionChange,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const { project, organization, authToken, showAlert } = useStore.getState();

  // Vista: 'candidates' | 'interviews'
  const [view, setView] = useState('candidates');
  const [selectedApp, setSelectedApp] = useState(null);

  // Entrevistas del candidato seleccionado
  const [interviews, setInterviews] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);

  // Invitar bot
  const [meetingUrl, setMeetingUrl] = useState('');
  const [meetingUrlError, setMeetingUrlError] = useState('');
  const [loadingInvite, setLoadingInvite] = useState(false);

  // Subir transcripción/informe existente
  const [uploadFile, setUploadFile] = useState(null);
  const [interviewDate, setInterviewDate] = useState('');
  const [loadingUpload, setLoadingUpload] = useState(false);

  // Reemplazar (eliminar) una entrevista: se confirma en un modal
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replacing, setReplacing] = useState(false);

  // Asignar participantes
  const [assigningInterview, setAssigningInterview] = useState(null);
  const [selectedIntervieweeId, setSelectedIntervieweeId] = useState('');
  const [loadingAssign, setLoadingAssign] = useState(false);

  // Preguntas de entrevista del cargo (se generan/editan en su propio modal)
  const [questionsSet, setQuestionsSet] = useState(position?.interviewQuestions || null);
  const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false);

  useEffect(() => {
    setQuestionsSet(position?.interviewQuestions || null);
  }, [position?.interviewQuestions]);

  const handleQuestionsChange = (set) => {
    setQuestionsSet(set);
    if (onPositionChange) onPositionChange({ interviewQuestions: set });
  };

  const positionQuestionsCount = questionsSet?.questions?.length || 0;

  useEffect(() => {
    if (open) {
      setView('candidates');
      setSelectedApp(null);
      setInterviews([]);
      setMeetingUrl('');
      setMeetingUrlError('');
      setAssigningInterview(null);
      setUploadFile(null);
      setInterviewDate('');
      setQuestionsDialogOpen(false);
      setReplaceTarget(null);
    }
  }, [open]);

  const handleSelectCandidate = async (application) => {
    if (!application.applicantId) {
      showAlert(t('ats.interview.noApplicantId'), 'error');
      return;
    }
    setSelectedApp(application);
    setView('interviews');
    await fetchInterviews(application);
  };

  const fetchInterviews = async (application) => {
    const app = application || selectedApp;
    const applicationId = app ? (app.uid || app.id) : null;
    if (!positionId || !applicationId) {
      setInterviews([]);
      return;
    }
    setLoadingInterviews(true);
    try {
      // Solo las entrevistas de esta vacante, filtradas por la postulación.
      // (Las creadas fuera del pipeline no tienen positionId/applicationId y no aparecen.)
      const res = await api.get(
        `/interviews/by-position`,
        {
          params: { positionId },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.errorCode === 0) {
        const data = res.data.response?.data;
        const list = Array.isArray(data) ? data : [];
        setInterviews(list.filter((i) => i.applicationId === applicationId));
      } else {
        setInterviews([]);
      }
    } catch (err) {
      console.error(err);
      setInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  // Se ejecuta al confirmar en el modal de reemplazar (replaceTarget)
  const handleDeleteInterview = async () => {
    const interview = replaceTarget;
    if (!selectedApp || !interview?.uid) {
      setReplaceTarget(null);
      return;
    }
    setReplacing(true);
    try {
      const res = await api.delete(
        `/applicants/${selectedApp.applicantId}/interviews/${interview.uid}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.errorCode === 0) {
        showAlert(t('ats.interview.replaceSuccess'), 'success');
        await fetchInterviews(selectedApp);
      } else {
        showAlert(t('ats.interview.replaceError'), 'error');
      }
    } catch (err) {
      showAlert(t('ats.interview.replaceError'), 'error');
    } finally {
      setReplacing(false);
      setReplaceTarget(null);
    }
  };

  const handleInviteBot = async () => {
    if (!meetingUrl.trim()) {
      setMeetingUrlError(t('interviews.meetingUrlRequired'));
      return;
    }
    setMeetingUrlError('');
    setLoadingInvite(true);
    try {
      const res = await api.post(
        `/applicants/${selectedApp.applicantId}/interviews/invite-bot`,
        {
          meetingUrl: meetingUrl.trim(),
          projectId: project,
          organizationId: organization,
          positionId,
          positionName: selectedApp?.positionName || null,
          applicationId: selectedApp?.uid || selectedApp?.id || null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.errorCode === 0) {
        showAlert(t('interviews.botInvitedSuccess'), 'success');
        setMeetingUrl('');
        await fetchInterviews(selectedApp);
      } else {
        showAlert(res.data.response?.message || t('interviews.inviteBotError'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.inviteBotError'), 'error');
    } finally {
      setLoadingInvite(false);
    }
  };

  const handleUploadTranscript = async () => {
    if (!uploadFile) {
      showAlert(t('interviews.upload.fileRequired'), 'warning');
      return;
    }
    setLoadingUpload(true);
    try {
      const formData = new FormData();
      formData.append('transcript', uploadFile);
      formData.append('projectId', project);
      formData.append('organizationId', organization);
      if (selectedApp?.candidateName) {
        formData.append('intervieweeName', selectedApp.candidateName);
      }
      if (interviewDate) {
        formData.append('interviewDate', interviewDate);
      }
      if (positionId) formData.append('positionId', positionId);
      if (selectedApp?.positionName) formData.append('positionName', selectedApp.positionName);
      const appId = selectedApp?.uid || selectedApp?.id;
      if (appId) formData.append('applicationId', appId);

      const res = await api.post(
        `/applicants/${selectedApp.applicantId}/interviews/upload-transcript`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (res.data.errorCode === 0) {
        showAlert(t('interviews.upload.success'), 'success');
        setUploadFile(null);
        setInterviewDate('');
        await fetchInterviews(selectedApp);
      } else {
        showAlert(res.data.response?.message || t('interviews.upload.error'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.upload.error'), 'error');
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleAssignParticipants = async (interview) => {
    if (!selectedIntervieweeId) {
      showAlert(t('interviews.selectIntervieweeRequired'), 'warning');
      return;
    }
    const interviewee = interview.participants.find(
      (p) => p.id === Number(selectedIntervieweeId)
    );
    setLoadingAssign(true);
    try {
      const res = await api.put(
        `/applicants/${selectedApp.applicantId}/interviews/${interview.uid}/assign-participants`,
        {
          intervieweeId: interviewee.id,
          intervieweeName: interviewee.name,
          applicantName: selectedApp.candidateName || selectedApp.candidateEmail,
          meetingDate: interview.createdAt?._seconds
            ? new Date(interview.createdAt._seconds * 1000).toISOString()
            : null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.errorCode === 0) {
        showAlert(t('interviews.assignedSuccess'), 'success');
        setAssigningInterview(null);
        setSelectedIntervieweeId('');
        await fetchInterviews(selectedApp);
      } else {
        showAlert(t('interviews.assignError'), 'error');
      }
    } catch (err) {
      showAlert(t('interviews.assignError'), 'error');
    } finally {
      setLoadingAssign(false);
    }
  };

  const getStatusChip = (status) => {
    const conf = STATUS_CONFIG[status] || STATUS_CONFIG.error;
    const label = lang === 'en' ? conf.labelEn : conf.labelEs;
    return <Chip label={label} color={conf.color} size="small" />;
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    return new Date(ts).toLocaleDateString('es-CL');
  };

  const getInitials = (name) =>
    (name || 'C').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {view === 'interviews' && (
          <IconButton
            size="small"
            onClick={() => {
              setView('candidates');
              setSelectedApp(null);
              setAssigningInterview(null);
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Typography variant="h6">
          {view === 'candidates'
            ? `${t('interviews.title')} — ${stage?.name || ''}`
            : t('interviews.modalTitle', { name: selectedApp?.candidateName || selectedApp?.candidateEmail })
          }
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 300 }}>

        {/* ── VISTA: CANDIDATOS ── */}
        {view === 'candidates' && (
          <>
            {/* Preguntas de entrevista del cargo (con IA o a mano): se definen
                solo aquí; las respuestas de cada candidato se anotan en su ficha. */}
            <Paper
              elevation={0}
              sx={{ border: '1px solid #E9D5FF', backgroundColor: '#FAF5FF', borderRadius: 2, p: 1.5, mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <AiIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('ats.interviewQuestions.positionTitle')}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
              >
                {positionQuestionsCount > 0
                  ? t('ats.interviewQuestions.positionReadyHint')
                  : t('ats.interviewQuestions.positionEmptyHint')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setQuestionsDialogOpen(true)}
                startIcon={
                  positionQuestionsCount > 0
                    ? <QuestionsIcon sx={{ fontSize: 16 }} />
                    : <AiIcon fontSize="small" />
                }
                sx={{
                  fontSize: '0.75rem',
                  borderColor: '#7C3AED',
                  color: '#7C3AED',
                  '&:hover': { borderColor: '#6D28D9', backgroundColor: '#F5F3FF' },
                }}
              >
                {positionQuestionsCount > 0
                  ? t('ats.interviewQuestions.viewPosition', { count: positionQuestionsCount })
                  : t('ats.interviewQuestions.defineQuestions')}
              </Button>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ats.interview.selectCandidate')}
            </Typography>

            {applications.length === 0 ? (
              <Alert severity="info">{t('ats.interview.noCandidates')}</Alert>
            ) : (
              <List disablePadding>
                {applications.map((app) => {
                  const appId = app.uid || app.id;
                  const hasApplicantId = !!app.applicantId;
                  return (
                    <ListItem key={appId} disablePadding>
                      <ListItemButton
                        onClick={() => handleSelectCandidate(app)}
                        disabled={!hasApplicantId}
                        sx={{ borderRadius: 1, mb: 0.5 }}
                      >
                        <Avatar sx={{ width: 32, height: 32, mr: 1.5, backgroundColor: '#EDE9FE', color: '#6A5AF9', fontSize: '0.75rem', fontWeight: 700 }}>
                          {getInitials(app.candidateName || app.candidateEmail)}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600} color={hasApplicantId ? 'inherit' : 'text.disabled'}>
                              {app.candidateName || '—'}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {app.candidateEmail}
                              {!hasApplicantId && ` — ${t('ats.cvFilter.noApplicantId')}`}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}

        {/* ── VISTA: ENTREVISTAS DEL CANDIDATO ── */}
        {view === 'interviews' && (
          <>
            {/* Invitar bot */}
            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {t('interviews.inviteBotTitle')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label={t('interviews.meetingUrlLabel')}
                placeholder="https://meet.google.com/..."
                value={meetingUrl}
                onChange={(e) => { setMeetingUrl(e.target.value); setMeetingUrlError(''); }}
                error={!!meetingUrlError}
                helperText={meetingUrlError || t('interviews.meetingUrlHelper')}
                sx={{ mb: 1.5 }}
              />
              <Button
                variant="contained"
                startIcon={loadingInvite ? <CircularProgress size={16} color="inherit" /> : <InterviewIcon />}
                onClick={handleInviteBot}
                disabled={loadingInvite}
                fullWidth
                sx={{ backgroundColor: '#8B5CF6', '&:hover': { backgroundColor: '#7C3AED' } }}
              >
                {loadingInvite ? t('interviews.inviting') : t('interviews.inviteBot')}
              </Button>
            </Paper>

            {/* Subir transcripción/informe existente */}
            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                {t('interviews.upload.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {t('interviews.upload.hint')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" component="label" startIcon={<UploadIcon />} size="small">
                  {t('interviews.upload.chooseFile')}
                  <input
                    hidden
                    type="file"
                    accept=".txt,.pdf,.doc,.docx"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                </Button>
                {uploadFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                    {uploadFile.name}
                  </Typography>
                )}
              </Box>

              <TextField
                type="date"
                size="small"
                label={t('interviews.upload.dateLabel')}
                InputLabelProps={{ shrink: true }}
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                sx={{ mb: 1.5, width: 220 }}
              />

              <Button
                variant="contained"
                startIcon={loadingUpload ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                onClick={handleUploadTranscript}
                disabled={loadingUpload || !uploadFile}
                fullWidth
                sx={{ backgroundColor: '#10B981', '&:hover': { backgroundColor: '#059669' } }}
              >
                {loadingUpload ? t('interviews.upload.uploading') : t('interviews.upload.submit')}
              </Button>
            </Paper>

            {/* Lista de entrevistas */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('interviews.title')}
              </Typography>
              <IconButton size="small" onClick={() => fetchInterviews(selectedApp)}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {loadingInterviews ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : interviews.length === 0 ? (
              <Alert severity="info">{t('interviews.noInterviews')}</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {interviews.map((interview) => (
                  <Paper key={interview.uid} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(interview.createdAt)}
                        </Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {getStatusChip(interview.status)}
                          {interview.provider === 'manual' && (
                            <Chip label={t('interviews.upload.manualBadge')} size="small" variant="outlined" />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Ver transcripción */}
                        {interview.status === 'completed' && interview.transcriptTxtUrl && (
                          <Tooltip title={t('interviews.viewTranscript')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => window.open(interview.transcriptTxtUrl, '_blank')}
                            >
                              <TranscriptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Reemplazar (eliminar la entrevista de esta vacante) */}
                        <Tooltip title={t('ats.interview.replace')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setReplaceTarget(interview)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Asignar participantes */}
                    {interview.status === 'pending_assignment' && interview.participants?.length > 0 && (
                      <>
                        {assigningInterview?.uid === interview.uid ? (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {t('interviews.whoIsInterviewee')}
                            </Typography>
                            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                              <InputLabel>{t('interviews.whoIsInterviewee')}</InputLabel>
                              <Select
                                value={selectedIntervieweeId}
                                label={t('interviews.whoIsInterviewee')}
                                onChange={(e) => setSelectedIntervieweeId(e.target.value)}
                              >
                                {interview.participants.map((p) => (
                                  <MenuItem key={p.id} value={p.id}>
                                    {p.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" color="inherit"
                                onClick={() => { setAssigningInterview(null); setSelectedIntervieweeId(''); }}>
                                {t('cancel')}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleAssignParticipants(interview)}
                                disabled={loadingAssign || !selectedIntervieweeId}
                                startIcon={loadingAssign ? <CircularProgress size={14} color="inherit" /> : null}
                                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
                              >
                                {t('interviews.confirm')}
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {t('interviews.detectedParticipants')} {interview.participants.map((p) => p.name).join(', ')}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => { setAssigningInterview(interview); setSelectedIntervieweeId(''); }}
                              sx={{ borderColor: '#F59E0B', color: '#F59E0B' }}
                            >
                              {t('interviews.assignRoles')}
                            </Button>
                          </Box>
                        )}
                      </>
                    )}

                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">{t('close')}</Button>
      </DialogActions>
    </Dialog>

    {questionsDialogOpen && (
      <AtsPositionInterviewQuestionsDialog
        open
        onClose={() => setQuestionsDialogOpen(false)}
        positionId={positionId}
        positionName={position?.name || ''}
        interviewQuestions={questionsSet}
        onChange={handleQuestionsChange}
      />
    )}

    {/* Confirmación de reemplazar (eliminar) la entrevista */}
    <Dialog
      open={Boolean(replaceTarget)}
      onClose={() => { if (!replacing) setReplaceTarget(null); }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('ats.interview.replaceTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{t('ats.interview.replaceConfirm')}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setReplaceTarget(null)} color="inherit" disabled={replacing}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleDeleteInterview}
          color="error"
          variant="contained"
          disabled={replacing}
          startIcon={replacing ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {t('delete')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AtsInterviewDialog;